/**
 * ./src/exercise1/Exercise1Choosing.js
 * /RhythmChoosing
 * 
 * This module manages the **Rhythmic Breathing exercise configuration**.
 * It allows users to interact with **animated visual circles**, adjust breathing speed, 
 * and save their settings before starting the formal exercise.
 * 
 */

import React, { useState, useEffect } from "react"; // React hooks for managing state and side effects
import "./Exercise1Choosing.css"; // Importing the CSS file for styling
import Circle1 from "../components/icons/circlein1.svg";
import Circle2 from "../components/icons/circlein2.svg";
import Circle3 from "../components/icons/circlein3.svg";
import Circle4 from "../components/icons/circlein4.svg";
import Circle5 from "../components/icons/circlein5.svg";
import Circle6 from "../components/icons/circlein6.svg";
import Circle7 from "../components/icons/circlein7.svg";

import stick1 from "../components/icons/stick1.svg";
import stick2 from "../components/icons/stick2.svg";
import stick3 from "../components/icons/stick3.svg";
import stick4 from "../components/icons/stick4.svg";
import stick5 from "../components/icons/stick5.svg";
import stick6 from "../components/icons/stick6.svg";
import stick7 from "../components/icons/stick7.svg";

import person1 from '../components/icons/person1.svg';
import person2 from '../components/icons/person2.svg';
import person3 from '../components/icons/person3.svg';
import person4 from '../components/icons/person4.svg';
import person5 from '../components/icons/person5.svg';
import person6 from '../components/icons/person6.svg';
import person7 from '../components/icons/person7.svg';

import CloseIcon from "../components/icons/close.svg";
import InfoIcon from "../components/icons/InfoIcon.svg";
import PlayIcon from "../components/icons/play.svg";
import StopIcon from "../components/icons/stop.svg";
import PlusButton from "./icons/plusbutton.svg";
import MinusButton from "./icons/minusbutton.svg";
import { useNavigate, useLocation } from "react-router-dom"; // Used for navigation within the app.
import SmallInfo from "./icons/smalli.svg";
import SmallInfoClick from "./icons/smalli-click.svg";
import IconBubble from "../components/IconBubble/IconBubble";
import Overlay from "../components/Overlay/Overlay";

function Exercise1Choosing() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/exercise1workout";
  const duration = location.state?.duration || 3;
  const time = location.state?.time;


  const [showPlayIcon, setShowPlayIcon] = useState(true);
  const [showStopIcon, setShowStopIcon] = useState(false);

  // Remove the time state and display since it's just for configuration
  // const [time, setTime] = useState(180); - DELETE THIS LINE

  const [long, setLong] = useState(
    () => parseFloat(localStorage.getItem("long")) || 3.0 // Default Inhale duration 3 seconds
  );
  const [short, setShort] = useState(
    () => parseFloat(localStorage.getItem("short")) || 2.0 // Default Exhale duration 2 seconds
  );
  const [hold, setHold] = useState(
    () => parseFloat(localStorage.getItem("hold")) || 1.0 // Default Pause duration 1 second
  );
  const [isPlaying, setIsPlaying] = useState(false); // Control the play state
  const [isPaused, setIsPaused] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // Track the current circle step
  const [nextStep, setNextStep] = useState(2); // Track the next circle step
  const [previousStep, setPreviousStep] = useState(null);
  const [fadeInStyle, setFadeInStyle] = useState({ opacity: 1 }); // Style for fade-in effect
  const [fadeOutStyle, setFadeOutStyle] = useState({ opacity: 0 }); // Style for fade-out effect
  const [isTransitioningToOne, setIsTransitioningToOne] = useState(false);
  const [showInfoBubble, setShowInfoBubble] = useState(false);

  // handles instructional bubbles
  const [tutorialStep, setTutorialStep] = useState("Tutorial");
  const [bubbleProps, setBubbleProps] = useState([
    "start-tutorial",
    130,
    50,
    `<span style="color: #3B3D4B; font-family: 'Nunito', sans-serif; font-size: 16px; font-size: 16px; display: flex; text-align: center; position: relative; left: -7px; ">
    Click to begin!
  </span>`,
    "bottom-left",
  ]);

  const [showBubble, setShowBubble] = useState(true);

  const [speedMessage, setSpeedMessage] = useState("");

  // enables all the breathing durations follow the 3:2:1 ratio
  const normalizeBreathingTimes = () => {
    // Get the current long value from localStorage or default
    let currentLong = parseFloat(localStorage.getItem("long")) || 3.0;

    // Calculate the next multiple of 0.3 that's >= current long value
    let normalizedLong = Math.ceil(currentLong / 0.3) * 0.3;
    // Round to 1 decimal place to avoid floating point issues
    normalizedLong = parseFloat(normalizedLong.toFixed(1));

    // Calculate short and hold based on 3:2:1 ratio
    let normalizedShort = parseFloat(((normalizedLong * 2) / 3).toFixed(1));
    let normalizedHold = parseFloat(((normalizedLong * 1) / 3).toFixed(1));

    // Update state and localStorage
    setLong(normalizedLong);
    setShort(normalizedShort);
    setHold(normalizedHold);

    localStorage.setItem("long", normalizedLong.toString());
    localStorage.setItem("short", normalizedShort.toString());
    localStorage.setItem("hold", normalizedHold.toString());
  };

  useEffect(() => {
    normalizeBreathingTimes();
  }, []); // Empty dependency array means this runs once when component mounts

  useEffect(() => {
    if (tutorialStep === "Inhale") {
      setBubbleProps([
        "start-tutorial",
        110,
        50,
        `<span style="color: #3B3D4B; font-family: 'Nunito', sans-serif; font-size: 16px; display: flex; text-align: center; position: relative; left: -7px;">
        Inhale 2 times
      </span>`,
        "bottom-left",
      ]);
      setShowBubble(true);
    }

    if (tutorialStep === "Exhale") {
      setBubbleProps([
        "start-tutorial",
        110,
        50,
        `<span style="color: #3B3D4B; font-family: 'Nunito', sans-serif; font-size: 16px; display: flex; text-align: center; position: relative; left: -7px;">
        Exhale 3 times
      </span>`,
        "bottom-left",
      ]);
      setShowBubble(true);
    }

    if (tutorialStep === "Finish") {
      setBubbleProps([
        "start-excercise",
        200,
        50,
        `<span style="color: #3B3D4B; font-family: 'Nunito', sans-serif; font-size: 16px; ">
        Click here to start the exercise!
      </span>`,
        "top-right",
      ]);
      setShowBubble(true);
    }
  }, [tutorialStep]);

  // add function to first show next button before starting to breath
  const freezeScreen = () => {
    // freeze to instruct users to inhale twice
    if (tutorialStep === "Tutorial") {
      setTutorialStep("Adjust Speed");
      setBubbleProps(["adjust-speed", 
                      160, 
                      50, 
                      `<span style="color: #3B3D4B; font-family: 'Nunito', sans-serif; font-size: 16px; ">
                        Adjust your breathing speed
                      </span>`,
                      "bottom-right"]);
    }

    if (tutorialStep === "Show Info") {
      setTutorialStep("Exhale");
      setBubbleProps(["adjust-speed", 
                      160, 
                      50, 
                      `<span style="color: #3B3D4B; font-family: 'Nunito', sans-serif; font-size: 16px;">
                      Adjust speed
                     </span>`,
                      "bottom-right"]);
    }
  };

  useEffect(() => {
    let timeout;
    if (isPlaying) {
      const steps = [
        [stick1, stick1], // Pair of sticks for Step 1
        [stick2, stick2], // Pair of sticks for Step 2
        [stick3, stick3],
        [stick4, stick4],
        [stick5, stick5],
        [stick6, stick6],
        [stick7, stick7],
      ];

      const durations = [long, long, long, short, short, short, short];

      const fadeInNextStick = () => {
        timeout = setTimeout(() => {
          setCurrentStep(nextStep);
          if (nextStep === 1) {
            setIsTransitioningToOne(true);
            setFadeInStyle({
              opacity: 1,
              transition: `opacity 0.2s ease-in`,
            });
            // Add fade-out effect for Circle 7
            setFadeOutStyle({
              opacity: 0,
              transition: `opacity 0.2s ease-out`,
            });
          } else {
            setIsTransitioningToOne(false);
            setFadeInStyle({
              opacity: 1,
              transition: `opacity ${durations[nextStep - 1]}s ease-in`,
            });
          }

          // Wait for the new circle to fully fade in before removing the previous one
          timeout = setTimeout(
            () => {
              setPreviousStep(null); // This will make the previous circle disappear suddenly

              // Prepare for the next transition
              timeout = setTimeout(() => {
                setNextStep((prev) => (prev === 7 ? 1 : prev + 1));
                setPreviousStep(currentStep);
                fadeInNextStick();
              }, hold * 1000); // hold time in milliseconds
            },
            nextStep === 1 ? 200 : durations[nextStep - 1] * 1000
          ); // Use 0.2s for transition to Circle 1
        }, 10);
      };

      if (currentStep == 2 && tutorialStep == "Adjust Speed") {
        pauseBreathing();
        setTutorialStep("Inhale");
      } else if (currentStep == 5 && tutorialStep == "Inhale") {
        pauseBreathing();
        setTutorialStep("Exhale");
      } else if (currentStep == 1 && tutorialStep == "Exhale") {
        pauseBreathing();
        setTutorialStep("Finish");
      } else {
        fadeInNextStick();
      }
    }

    return () => clearTimeout(timeout);
  }, [isPlaying, nextStep, long, short, hold, currentStep]);

  useEffect(() => {
    // Save to localStorage whenever values change
    localStorage.setItem("long", long.toString());
    localStorage.setItem("short", short.toString());
    localStorage.setItem("hold", hold.toString());
  }, [long, short, hold]);

  // Users can increase/decrease breathing speed in 0.3s increments/decrements
  const handleIncrement = () => {
    if (long < 10) {
      setLong((prev) => parseFloat((prev + 0.3).toFixed(1)));
      setShort((prev) => parseFloat((prev + 0.2).toFixed(1)));
      setHold((prev) => parseFloat((prev + 0.1).toFixed(1)));
    }
    if (tutorialStep === "Adjust Speed") {
      setShowBubble(false);
      startBreathing();
    }
    let displaySpeedNum = 0 || parseFloat((1 - short) + 5.9).toFixed(1);
    setSpeedMessage(displaySpeedNum);
//    setTimeout(() => setSpeedMessage(""), 1000);
  };

  const handleDecrement = () => {
    if (long - 0.3 > 0) {
      setLong((prev) => parseFloat((prev - 0.3).toFixed(1)));
      setShort((prev) => parseFloat((prev - 0.2).toFixed(1)));
      setHold((prev) => parseFloat((prev - 0.1).toFixed(1)));
    }
    if (tutorialStep === "Adjust Speed") {
      setShowBubble(false);
      startBreathing();
    }
    let displaySpeedNum = 0 || parseFloat((1 - short) + 5.9).toFixed(1);
    setSpeedMessage(displaySpeedNum);
//    setTimeout(() => setSpeedMessage(""), 1000);
  };

  // clicking play start the breathing cycle 
  const startBreathing = () => {
    if (tutorialStep === "Tutorial") {
      freezeScreen();
      setShowPlayIcon(false);
      setShowStopIcon(false);
    } else {
      setIsPlaying(true);
      setFadeInStyle({ opacity: 1 }); // Keep Circle 1 visible
    }
  };

  // clicking stop resets the breathing cycle.
  const stopBreathing = () => {
    setIsPlaying(false); // Stop the breathing exercise
    setCurrentStep(1); // Reset to Circle1
    setNextStep(2); // Prepare next step to start at Circle 2
    setPreviousStep(null); // Clear the previous step
    setFadeOutStyle({ opacity: 0 }); // Make all other circles disappear
    setFadeInStyle({ opacity: 1 }); // Keep Circle 1 visible
  };

  // clicking pause temporarily stops without resetting progress.
  const pauseBreathing = () => {
    setIsPlaying(false); 
    setIsPaused(true);
  };

  const handleSave = () => {
    // track that the user has encountered /RhythmChoosing page
    localStorage.setItem("hasCompletedRhythmChoosing", "true");

    // navigates to /exercise1workout
    navigate(returnTo, { state: { duration: duration, time: time} });
  };

  const handleClose = () => {
    navigate("/"); // Navigate to the landing page
  };

  const toggleInfo = () => {
    if (tutorialStep == "Finish") {
      setShowInfoBubble(!showInfoBubble); // This should work both ways
    }
  };

  // 1. click to begin -- upon load / start-tutorial
  // 2. adjust speed -- after start  / adjust-speed
  // 3. inhale 2 times -- after first inhale / start-tutorial
  // 4. click i for more information -- after all inhales / information
  // 5. exhale 3 times -- after exhale starts / start-tutorial
  // 6. click to start exercise -- after breath cycle / start-exercise

  const renderBubble = () => {
    if (showBubble) {
      return (
        <>
          <div className={`icon-bubble ${bubbleProps[0]}`}>
            <IconBubble
              width={bubbleProps[1]}
              height={bubbleProps[2]}
              HTML={bubbleProps[3]}
              position={bubbleProps[4]}
            />
          </div>
          {tutorialStep == "Finish" && (
            <div className={`icon-bubble information`}>
              <IconBubble
                width={180}
                height={50}
                HTML={`
                  <span style="color: #4B5161; font-family: 'Nunito', sans-serif; font-size: 16px;">
                    Click the "i" for more information
                  </span>
                `}
                position={"bottom-left"}
              />
            </div>
          )}
        </>
      );
    }
  };

  const continueTutorial = () => {
    setIsPaused(false);
    setShowBubble(false);
    startBreathing();
  };

  return (
    <div className="exercise-container">
      <Overlay visible={isPaused} text={"Got it!"} onClick={continueTutorial} />
      {/* instructional bubbles conditionally rendered with absolute positions */}
      {renderBubble()}
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img
            src={CloseIcon}
            alt="close icon"
            className="close-icon"
            onClick={handleClose}
          />
        </div>

        {/* Remove the progress bar and timer display */}
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress"></div>
          </div>
        </div>

        <button className="save-button" onClick={handleSave}>
          Save
        </button>
      </div>
      {/* Circle control and breathing inputs */}
      <div className="main-content">
        <div className="circle-container">
          <div className="circle-wrapper">
            {[
              [stick1, stick1],
              [stick2, stick2],
              [stick3, stick3],
              [stick4, stick4],
              [stick5, stick5],
              [stick6, stick6],
              [stick7, stick7],
            ].map((StickPair, index) => (
              <div key={index} className="stick-container">
                {/* Left Stick */}
              <img
                src={StickPair[0]}
                alt={`stick left step ${index + 1}`}
                className="breathing-stick left-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
                {/* Right Stick */}
              <img
                src={StickPair[1]}
                alt={`stick right step ${index + 1}`}
                className="breathing-stick right-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
              </div>
            ))}

              {[person1, person2, person3, person4, person5, person6, person7].map((PersonImg, index) => (
              <img
                key={`person-${index}`}
                src={PersonImg}
                alt={`person step ${index + 1}`}
                className="breathing-circle"
                style={{
                  position: 'absolute',
                  height: '55%',
                  width: 'auto',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  ...(currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 || (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne ? fadeOutStyle : { opacity: 1 }
                    : { opacity: 0 }),
                  cursor: 'pointer',
                  pointerEvents: currentStep === index + 1 ? 'auto' : 'none'
                }}
                onClick={() => {
                  if (currentStep === index + 1) {
                    stopBreathing();
                  }
                }}
              />
            ))}

            {/* Conditionally render play/stop button */}
            {/* <img
              src={isPlaying || isPaused ? StopIcon : PlayIcon} // Show stop if playing, else show play
              alt={isPlaying ? "stop icon" : "play icon"}
              className="play-icon"
              onClick={isPlaying ? stopBreathing : startBreathing} // Toggle between play/stop functions
            /> */}

            {showPlayIcon && (
              <img
                src={PlayIcon}
                alt="play icon"
                className="play-icon"
                onClick={startBreathing}
              />
            )}
            {showStopIcon && (
              <img
                src={StopIcon}
                alt="stop icon"
                className="play-icon"
                style={{ opacity: 1 }}
                onClick={stopBreathing}
              />
            )}
            
          </div>
        </div>

        {/* Control buttons below the circle */}
        <div className="breathing-controls">
          <div className="control">
            <div className="row2">
              <div className="speed-controls">
                <img
                  src={MinusButton}
                  alt="minus button"
                  onClick={handleIncrement}
                  className="control-button"
                />
                <span>Speed</span>
                <img
                  src={PlusButton}
                  alt="plus button"
                  onClick={handleDecrement}
                  className="control-button"
                />
              </div>
              <div className="info-button-container">
                <img
                  src={showInfoBubble ? SmallInfoClick : SmallInfo}
                  alt="info"
                  className="small-info-icon"
                  onClick={toggleInfo}
                />
                {showInfoBubble && (
                  <div className="info-bubble-container">
                    <IconBubble
                      width={160}
                      height={50}
                      HTML={`Inhale: ${long.toFixed(
                        1
                      )}s x 2 Exhale: ${short.toFixed(1)}s x 3`}
                      position="bottom-left"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="row2">
              <span style={{ fontSize: "24px" }}>{speedMessage}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        Adjust your rhythmic breathing and try until you are breathing
        comfortably. Then Press "Save" to start the formal exercise.
      </div>
      {/* Add breathing times display */}
      {/* <div className="breathing-times">
        <div className="time-item">
          <span className="time-label">Long:</span>
          <span className="time-value">{long.toFixed(1)}s</span>
        </div>
        <div className="time-item">
          <span className="time-label">Short:</span>
          <span className="time-value">{short.toFixed(1)}s</span>
        </div>
        <div className="time-item">
          <span className="time-label">Hold:</span>
          <span className="time-value">{hold.toFixed(1)}s</span>
        </div>
      </div> */}
    </div>
  );
}

export default Exercise1Choosing;
